import { plural } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { Currency } from '@navio/nsdk';
import { cn } from 'noddi-ui-common';
import { NoddiButton } from 'noddi-ui-web';
import { formatCurrencyAmount } from 'noddi-util';

type BulkBarProps = {
  count: number;
  total: number;
  currency: Currency | undefined;
  onClear: () => void;
  onAccept: () => void;
  onDecline: () => void;
  isProcessing?: boolean;
};

export const BulkBar = ({ count, total, currency, onClear, onAccept, onDecline, isProcessing }: BulkBarProps) => {
  const visible = count > 0;
  const formattedTotal = formatCurrencyAmount({ money: { amount: total, currency }, decimals: 0 });
  return (
    <div
      className={cn(
        'pointer-events-none fixed left-1/2 z-docked w-full max-w-screen-mdx -translate-x-1/2 px-3 transition-[bottom] duration-300 sm:px-10 xl:max-w-screen-2xl xl:px-4',
        visible ? 'bottom-3 sm:bottom-6' : '-bottom-32'
      )}
      style={{ transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' }}
    >
      <div className='pointer-events-auto relative flex flex-col gap-3 overflow-hidden rounded-2xl border-2 border-card-selected-background bg-card-light-background px-4 py-3 shadow-2xl sm:flex-row sm:items-center sm:gap-3 sm:px-5'>
        <div className='flex min-w-0 flex-1 items-center justify-between gap-3 sm:block'>
          <div className='flex flex-col'>
            <div className='text-sm font-semibold text-primary'>
              {plural(count, { one: '# offer selected', other: '# offers selected' })}
            </div>
            <div className='text-[11px] text-muted-foreground tabular-nums'>
              <Trans>Total · {formattedTotal}</Trans>
            </div>
          </div>
          <NoddiButton
            variant='ghost'
            size='small'
            onClick={onClear}
            disabled={isProcessing}
            startIcon='X'
            className='shrink-0 sm:hidden'
            aria-label='Clear selection'
          >
            <Trans>Clear</Trans>
          </NoddiButton>
        </div>

        <div className='hidden h-8 w-px bg-card-selected-background/30 sm:block' aria-hidden />

        <div className='flex items-center gap-2 sm:contents'>
          <NoddiButton
            variant='outline'
            onClick={onDecline}
            disabled={isProcessing}
            startIcon='X'
            className='flex-1 sm:flex-none'
          >
            <Trans>Decline</Trans>
          </NoddiButton>

          <NoddiButton variant='outline' onClick={onClear} disabled={isProcessing} className='hidden sm:inline-flex'>
            <Trans>Clear</Trans>
          </NoddiButton>

          <NoddiButton
            variant='primary'
            onClick={onAccept}
            loading={isProcessing}
            startIcon='Check'
            className='flex-[2] sm:flex-none'
          >
            {plural(count, { one: 'Accept # offer', other: 'Accept # offers' })}
          </NoddiButton>
        </div>
      </div>
    </div>
  );
};
